import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Spin, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  FileExcelOutlined,
  FileDoneOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../../utils/feedback';
import { formatCurrency } from '../../../utils/currency';
import { formatLocalizedPercent } from '../../../utils/numberFormatting';
import { authService } from '../../../services/auth';
import { CostPartialMark, ReportFigure, ReportLongTable, ReportSection } from '../ReportTab';
import type { IncomeStatementResponse } from '../incomeStatement/incomeStatementApi';
import { exportSalesPivot } from '../incomeStatement/incomeStatementApi';
import type { PivotExportTable } from '../incomeStatement/pivotExport';
import { forecastMonthsLabel } from '../incomeStatement/incomeStatement';
import { money } from '../managementDashboard/DashboardCharts';
import {
  buildSalesRepOrdersExport,
  buildSalesReps,
  buildSalesRepsExport,
  repMarginPercent,
  salesRepOrderMargin,
  type SalesRepOrder,
  type SalesRepPayment,
  type SalesRepRow,
} from './salesRepsModel';
import './SalesRepsSection.css';

interface Props {
  /** The income statement of the period's year, loaded once by the page (null while loading). */
  statement: IncomeStatementResponse | null;
  failed?: boolean;
  /** The months of the period of „Wynik firmy” (`YYYY-MM`). */
  months: string[];
  periodLabel: string;
}

const PAYMENT_TAG: Record<SalesRepPayment, string> = { PAID: 'success', FACTORING: 'processing', UNPAID: 'default' };

// „Handlowcy” (decision 2026-10-29-handlowcy-marza-oplacona): per Opiekun, the
// invoiced margin of the page's period, its part on ZK marked „Opłacone” and
// the rest; a row opens the ZK behind it. ADMIN only, like the ZK lists.
const SalesRepsSection: React.FC<Props> = ({ statement, failed, months, periodLabel }) => {
  const { t, i18n } = useTranslation(['reports', 'common']);
  const language = i18n.language || 'pl';
  const feedback = useFeedback();
  const permitted = authService.canViewForecastVsActual();
  const k = (key: string, options?: Record<string, unknown>) => t(`reports:salesReps.${key}`, options ?? {}) as string;
  const result = useMemo(() => (permitted && statement ? buildSalesReps(statement, months) : null), [permitted, statement, months]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'reps' | 'orders' | null>(null);
  const selected = result?.reps.find((rep) => rep.key === selectedKey) ?? null;

  // Another period may not have the chosen rep: close the list then.
  useEffect(() => {
    if (selectedKey && result && !result.reps.some((rep) => rep.key === selectedKey)) setSelectedKey(null);
  }, [result, selectedKey]);

  const repName = (rep: Pick<SalesRepRow, 'name'>) => rep.name ?? k('noRep');
  const orderLabel = (number: string) => t('reports:managementDashboard.orderLabel', { number }) as string;
  const pct = (value: number | null) => (value == null ? '—' : formatLocalizedPercent(value, language));
  const exact = (value: number) => formatCurrency(value, 'PLN');
  const partial = k('costPartial');
  const amount = (value: number, pending: boolean, testId: string, negativeTone = false) => (
    <span className={`report-num${negativeTone && value < 0 ? ' report-negative' : ''}`} title={exact(value)}>
      {money(value)}
      {pending && <CostPartialMark hint={partial} testId={testId} />}
    </span>
  );

  const download = async (table: PivotExportTable, kind: 'reps' | 'orders') => {
    setExporting(kind);
    try {
      const file = await exportSalesPivot(table);
      const url = window.URL.createObjectURL(file.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      feedback.success(k('exported', { filename: file.filename }));
    } catch {
      feedback.error(k('exportFailed'));
    } finally {
      setExporting(null);
    }
  };

  const info = [k('lead'), ...(result?.total.costPending ? [partial] : [])];
  const exportReps = () => result && download(buildSalesRepsExport(result, {
    title: k('exportTitle', { period: periodLabel }),
    sheet: k('exportSheet', { period: periodLabel }),
    info,
    rep: k('colRep'),
    orders: k('colOrders'),
    revenue: k('colRevenue'),
    margin: k('colMargin'),
    marginPercent: k('colMarginPercent'),
    paid: k('colPaid'),
    unpaid: k('colUnpaid'),
    waiting: k('colWaiting'),
    total: k('total'),
    repName,
  }), 'reps');
  const exportOrders = (rep: SalesRepRow) => download(buildSalesRepOrdersExport(rep, {
    title: k('ordersExportTitle', { rep: repName(rep), period: periodLabel }),
    sheet: k('ordersExportSheet', { rep: repName(rep) }),
    info: [k('ordersLead'), ...(rep.costPending ? [partial] : [])],
    order: k('colOrder'),
    customer: k('colCustomer'),
    months: k('colMonths'),
    revenue: k('colRevenue'),
    cost: k('colCost'),
    margin: k('colMargin'),
    marginPercent: k('colMarginPercent'),
    payment: k('colPayment'),
    total: k('totalRow', { count: rep.orderCount }),
    orderLabel,
    monthsLabel: (list) => forecastMonthsLabel(list, language),
    paymentLabel: (payment) => k(`payment.${payment}`),
  }), 'orders');

  if (!permitted) {
    return (
      <ReportSection title={k('title')} icon={<TeamOutlined />}>
        <span className="report-muted">{k('adminOnly')}</span>
      </ReportSection>
    );
  }
  if (failed) return <Alert type="error" showIcon message={k('loadFailed')} />;
  // Before the report arrives there is nothing to sum: no figures, not zeros.
  if (!statement) return <div className="sales-reps__loading"><Spin /></div>;
  if (!result) {
    return (
      <ReportSection title={k('title')} icon={<TeamOutlined />}>
        <span className="report-muted">{k('adminOnly')}</span>
      </ReportSection>
    );
  }

  const { total } = result;
  const repColumns: ColumnsType<SalesRepRow> = [
    {
      title: k('colRep'),
      key: 'rep',
      render: (_, rep) => <span className={`report-cut${rep.name ? '' : ' report-muted'}`} title={repName(rep)}>{repName(rep)}</span>,
    },
    { title: k('colOrders'), key: 'orders', align: 'right', width: 64, render: (_, rep) => <span className="report-num">{rep.orderCount}</span> },
    { title: <span title={k('colRevenueHint')}>{k('colRevenue')}</span>, key: 'revenue', align: 'right', width: 132, render: (_, rep) => amount(rep.revenue, false, 'sales-reps-revenue-partial') },
    { title: <span title={k('colMarginHint')}>{k('colMargin')}</span>, key: 'margin', align: 'right', width: 124, render: (_, rep) => amount(rep.margin, rep.costPending, 'sales-reps-margin-partial', true) },
    {
      title: k('colMarginPercent'),
      key: 'marginPercent',
      align: 'right',
      width: 84,
      render: (_, rep) => (
        <span className="report-num">
          {pct(repMarginPercent(rep))}
          {rep.costPending && rep.revenue > 0 && <CostPartialMark hint={k('percentPartial')} testId="sales-reps-percent-partial" />}
        </span>
      ),
    },
    { title: <span title={k('colPaidHint')}>{k('colPaid')}</span>, key: 'paid', align: 'right', width: 124, render: (_, rep) => amount(rep.paidMargin, rep.paidPending, 'sales-reps-paid-partial', true) },
    { title: <span title={k('colUnpaidHint')}>{k('colUnpaid')}</span>, key: 'unpaid', align: 'right', width: 124, render: (_, rep) => amount(rep.unpaidMargin, rep.unpaidPending, 'sales-reps-unpaid-partial', true) },
    { title: <span title={k('colWaitingHint')}>{k('colWaiting')}</span>, key: 'waiting', align: 'right', width: 140, className: 'sales-reps__col-waiting', render: (_, rep) => amount(rep.waitingMargin, rep.waitingPending, 'sales-reps-waiting-partial', true) },
  ];

  const orderColumns: ColumnsType<SalesRepOrder> = [
    {
      title: k('colOrder'),
      key: 'order',
      width: 128,
      render: (_, order) => (
        <Link className="report-cut" to={`/app/orders/${order.id}`} title={orderLabel(order.displayOrderNumber)}>{orderLabel(order.displayOrderNumber)}</Link>
      ),
    },
    { title: k('colCustomer'), key: 'customer', render: (_, order) => <span className="report-cut" title={order.customerName}>{order.customerName}</span> },
    {
      title: k('colMonths'),
      key: 'months',
      width: 132,
      render: (_, order) => <span className="report-cut" title={forecastMonthsLabel(order.months, language)}>{forecastMonthsLabel(order.months, language)}</span>,
    },
    { title: k('colRevenue'), key: 'revenue', align: 'right', width: 112, render: (_, order) => amount(order.revenue, false, 'sales-rep-order-revenue-partial') },
    { title: k('colCost'), key: 'cost', align: 'right', width: 112, render: (_, order) => amount(order.revenue - salesRepOrderMargin(order), order.costPending, 'sales-rep-order-cost-partial') },
    { title: k('colMargin'), key: 'margin', align: 'right', width: 112, render: (_, order) => amount(salesRepOrderMargin(order), order.costPending, 'sales-rep-order-margin-partial', true) },
    {
      title: k('colMarginPercent'),
      key: 'marginPercent',
      align: 'right',
      width: 84,
      render: (_, order) => (
        <span className="report-num">
          {pct(repMarginPercent({ margin: salesRepOrderMargin(order), revenue: order.revenue }))}
          {order.costPending && order.revenue > 0 && <CostPartialMark hint={k('percentPartial')} testId="sales-rep-order-percent-partial" />}
        </span>
      ),
    },
    {
      title: k('colPayment'),
      key: 'payment',
      width: 112,
      render: (_, order) => (
        <Tooltip title={k(`paymentHint.${order.payment}`)}>
          <Tag className="sales-reps__payment" color={PAYMENT_TAG[order.payment]} data-testid="sales-rep-order-payment">{k(`payment.${order.payment}`)}</Tag>
        </Tooltip>
      ),
    },
  ];

  const pendingNote = total.costPending ? <CostPartialMark hint={partial} testId="sales-reps-figure-partial" /> : null;

  return (
    <div className="sales-reps" data-testid="sales-reps">
      <div className="report-figures">
        <ReportFigure
          icon={<FileDoneOutlined />}
          tone="indigo"
          testId="sales-reps-figure-invoiced"
          label={k('figureInvoiced')}
          value={<>{money(total.margin)}{pendingNote}</>}
          note={k('figureInvoicedNote', { revenue: money(total.revenue) })}
          noteTitle={k('figureInvoicedNoteTitle', { revenue: money(total.revenue), percent: pct(repMarginPercent(total)) })}
        />
        <ReportFigure
          icon={<CheckCircleOutlined />}
          tone="green"
          testId="sales-reps-figure-paid"
          label={k('figurePaid')}
          value={<>{money(total.paidMargin)}{total.paidPending && <CostPartialMark hint={partial} />}</>}
          note={k('figurePaidNote', { count: total.paidCount })}
        />
        <ReportFigure
          icon={<ClockCircleOutlined />}
          tone="orange"
          testId="sales-reps-figure-unpaid"
          label={k('figureUnpaid')}
          value={<>{money(total.unpaidMargin)}{total.unpaidPending && <CostPartialMark hint={partial} />}</>}
          note={k('figureUnpaidNote', { count: total.unpaidCount })}
        />
        <ReportFigure
          icon={<TeamOutlined />}
          tone="blue"
          testId="sales-reps-figure-count"
          label={k('figureCount')}
          value={k('ordersCount', { count: total.orderCount })}
          note={k('figureCountNote', { count: result.repCount })}
        />
      </div>

      <ReportSection
        title={k('tableTitle', { period: periodLabel })}
        icon={<TeamOutlined />}
        tone="indigo"
        count={result.reps.length}
        lead={k('lead')}
        actions={(
          result.reps.length > 0 ? (
            <Button size="small" icon={<FileExcelOutlined />} loading={exporting === 'reps'} onClick={exportReps} data-testid="sales-reps-export">
              {k('export')}
            </Button>
          ) : undefined
        )}
      >
        <ReportLongTable rowCount={result.reps.length} testId="sales-reps-rows">
          <div className="report-table sales-reps__table" data-testid="sales-reps-table">
            <Table<SalesRepRow>
              rowKey="key"
              size="small"
              tableLayout="fixed"
              columns={repColumns}
              dataSource={result.reps}
              pagination={false}
              locale={{ emptyText: k('empty') }}
              rowClassName={(rep) => `sales-reps__row${rep.key === selectedKey ? ' is-selected' : ''}`}
              onRow={(rep) => ({
                onClick: () => setSelectedKey(rep.key === selectedKey ? null : rep.key),
                'data-testid': 'sales-reps-row',
              } as React.HTMLAttributes<HTMLElement>)}
              summary={() => (result.reps.length ? (
                <Table.Summary>
                  <Table.Summary.Row className="sales-reps__total" data-testid="sales-reps-total">
                    <Table.Summary.Cell index={0}>{k('total')}</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right"><span className="report-num">{total.orderCount}</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">{amount(total.revenue, false, 'sales-reps-total-revenue-partial')}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">{amount(total.margin, total.costPending, 'sales-reps-total-margin-partial', true)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <span className="report-num">
                        {pct(repMarginPercent(total))}
                        {total.costPending && total.revenue > 0 && <CostPartialMark hint={k('percentPartial')} />}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">{amount(total.paidMargin, total.paidPending, 'sales-reps-total-paid-partial', true)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">{amount(total.unpaidMargin, total.unpaidPending, 'sales-reps-total-unpaid-partial', true)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right" className="sales-reps__col-waiting">{amount(total.waitingMargin, total.waitingPending, 'sales-reps-total-waiting-partial', true)}</Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null)}
            />
          </div>
        </ReportLongTable>
        {result.reps.length > 0 && !selected && <p className="report-muted sales-reps__hint report-cut" title={k('openHint')}>{k('openHint')}</p>}
      </ReportSection>

      {selected && (
        <ReportSection
          title={k('ordersTitle', { rep: repName(selected), period: periodLabel })}
          icon={<FileDoneOutlined />}
          tone="indigo"
          count={selected.orderCount}
          lead={k('ordersLead')}
          className="sales-reps__orders"
          actions={(
            <>
              {selected.orders.length > 0 && (
                <Button size="small" icon={<FileExcelOutlined />} loading={exporting === 'orders'} onClick={() => exportOrders(selected)} data-testid="sales-rep-orders-export">
                  {k('export')}
                </Button>
              )}
              <Button size="small" type="text" icon={<CloseOutlined />} aria-label={k('close')} title={k('close')} onClick={() => setSelectedKey(null)} />
            </>
          )}
        >
          <div className="report-table sales-reps__table" data-testid="sales-rep-orders">
            <Table<SalesRepOrder>
              rowKey="id"
              size="small"
              tableLayout="fixed"
              columns={orderColumns}
              dataSource={selected.orders}
              pagination={false}
              locale={{ emptyText: k('noInvoiced') }}
              onRow={() => ({ 'data-testid': 'sales-rep-order' } as React.HTMLAttributes<HTMLElement>)}
              summary={() => (selected.orders.length ? (
                <Table.Summary>
                  <Table.Summary.Row className="sales-reps__total" data-testid="sales-rep-orders-total">
                    <Table.Summary.Cell index={0} colSpan={3}>{k('totalRow', { count: selected.orderCount })}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">{amount(selected.revenue, false, 'sales-rep-orders-total-revenue-partial')}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">{amount(selected.cost, selected.costPending, 'sales-rep-orders-total-cost-partial')}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">{amount(selected.margin, selected.costPending, 'sales-rep-orders-total-margin-partial', true)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      <span className="report-num">
                        {pct(repMarginPercent(selected))}
                        {selected.costPending && selected.revenue > 0 && <CostPartialMark hint={k('percentPartial')} />}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>
                      <span className="report-muted report-cut" title={k('paidOfTotal', { paid: money(selected.paidMargin), unpaid: money(selected.unpaidMargin) })}>
                        {k('paidShort', { count: selected.paidCount })}
                      </span>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null)}
            />
          </div>
        </ReportSection>
      )}
    </div>
  );
};

export default SalesRepsSection;
